import React, { useState, useEffect } from 'react';
import {
    Shield,
    Clock,
    DollarSign,
    Calendar,
    Users,
    CreditCard,
    AlertTriangle,
    CheckCircle,
    Info,
    Save,
    Loader2,
    WalletCards,
    Landmark
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../Store/ApiContext';
import { enqueueSnackbar } from 'notistack';

// Add default settings for booking policies (includes checkInPolicy and checkOutPolicy)
const defaultSettings = {
    bookingPolicies: {
        checkInPolicy: {
            standardTime: '15:00',
            earlyCheckInFee: 0,
            lateArrivalCutoff: '22:00',
            lateArrivalFee: 0
        },
        checkOutPolicy: {
            standardTime: '11:00',
            lateCheckOutFee: 0,
            gracePeriodMinutes: 0
        },
        cancellationPolicy: {
            enabled: false,
            freeHours: 24,
            partialRefundHours: 12,
            refundPercentage: 50,
            noRefundHours: 6
        },
        paymentPolicy: {
            paymentIntegrationEnabled: false,
            depositRequired: false,
            depositPercentage: 20,
            paymentDueDate: 'booking',
            paymentMethodsInfo: {
                instapay: {
                    enabled: true,
                    instapayAddress: ''
                },
                bankTransfer: {
                    enabled: true,
                    bankDetails: {
                        accountName: '',
                        accountNumber: '',
                        bankName: '',
                    }
                },
                cash: {
                    enabled: true
                },
                digitalWallet: {
                    enabled: true,
                    walletNumber: ''
                }
            }
        },
        guestPolicies: {
            minimumAge: 18,
            maxGuestsPerUnit: 6,
            petPolicy: 'not-allowed',
            petFee: 0,
            smokingPolicy: 'not-allowed',
            partyPolicy: 'not-allowed'
        }
    }
};

export const BookingPolicies = () => {
    const { t } = useTranslation();
    const { getData, responseApi } = useApi();

    const [settings, setSettings] = useState(defaultSettings);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Load booking policies on component mount
    useEffect(() => {
        loadBookingPolicies();
    }, []);

    // No manual expand/collapse state needed now; details auto-show when enabled

    // Load booking policies from backend
    const loadBookingPolicies = async () => {
        try {
            setLoading(true);
            const response = await getData(null, 'bookingPolicies');
            if (response && response.results) {
                setSettings(ensureDefaults(response.results));
            }
        } catch (error) {
            console.error('Error loading booking policies:', error);
            enqueueSnackbar(t('error.loadingFailed') || 'Failed to load policies', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Save booking policies to backend
    const saveBookingPolicies = async (updatedSettings) => {
        console.log(updatedSettings);

        try {
            setSaving(true);
            const response = await responseApi('put', { bookingPolicies: updatedSettings.bookingPolicies }, 'bookingPolicies', updatedSettings._id);
            if (response && response.message === 'success') {
                enqueueSnackbar(t('settingsSavedSuccess'), { variant: 'success' });
                return response.results;
            } else {
                enqueueSnackbar(t('settingsSaveFailed'), { variant: 'error' });
            }
        } catch (error) {
            console.error('Error saving booking policies:', error);
            enqueueSnackbar(t('settingsSaveFailed'), { variant: 'error' });
            throw error;
        } finally {
            setSaving(false);
        }
    };

    // Ensure incoming settings have required defaults
    const ensureDefaults = (incoming) => {
        if (!incoming) return defaultSettings;

        const incomingPaymentPolicy = incoming.bookingPolicies?.paymentPolicy || {};
        const mergedPaymentMethodsInfo = { ...defaultSettings.bookingPolicies.paymentPolicy.paymentMethodsInfo };

        // Merge each payment method info properly
        Object.keys(mergedPaymentMethodsInfo).forEach(methodId => {
            const incomingMethodInfo = incomingPaymentPolicy.paymentMethodsInfo?.[methodId] || {};
            mergedPaymentMethodsInfo[methodId] = {
                ...defaultSettings.bookingPolicies.paymentPolicy.paymentMethodsInfo[methodId],
                ...incomingMethodInfo
            };
        });

        return {
            ...defaultSettings,
            ...incoming,
            bookingPolicies: {
                ...defaultSettings.bookingPolicies,
                ...(incoming.bookingPolicies || {}),
                paymentPolicy: {
                    ...defaultSettings.bookingPolicies.paymentPolicy,
                    ...incomingPaymentPolicy,
                    paymentMethodsInfo: mergedPaymentMethodsInfo
                }
            }
        };
    };

    // Merge updates into current settings and save to backend
    const updateSettings = async (updates) => {
        console.debug('updateSettings called with updates:', updates);
        console.debug('Current settings before merge:', settings);

        const merged = {
            ...settings,
            ...updates,
            bookingPolicies: {
                ...settings.bookingPolicies,
                ...(updates.bookingPolicies || {})
            }
        };

        console.debug('Merged settings result:', merged);
        setSettings(merged);

        // Auto-save to backend (uncommented for real-time updates)
        // try {
        //     await saveBookingPolicies(merged);
        // } catch (error) {
        //     // Revert local changes if save failed
        //     console.error('Save failed, reverting changes:', error);
        //     enqueueSnackbar(t('Failed to save changes') || 'Failed to save changes', { variant: 'error' });
        // }
    };

    // Handle policy changes with debouncing
    const handlePolicyChange = (section, field, value) => {
        console.debug('handlePolicyChange called with:', { section, field, value });
        updateSettings({
            bookingPolicies: {
                ...settings.bookingPolicies,
                [section]: {
                    ...settings.bookingPolicies[section],
                    [field]: value
                }
            }
        });
    };

    const paymentMethods = [
        { id: 'instapay', label: t('instapay'), icon: <CreditCard size={16} /> },
        { id: 'bankTransfer', label: t('Bank Transfer'), icon: <Landmark size={16} /> },
        { id: 'digitalWallet', label: t('Digital Wallet'), icon: <WalletCards size={16} /> },
        { id: 'cash', label: t('Cash'), icon: <DollarSign size={16} /> },
    ];

    const togglePaymentMethod = async (methodId) => {
        const currentEnabled = settings.bookingPolicies.paymentPolicy.paymentMethodsInfo?.[methodId]?.enabled;
        const newEnabled = !(currentEnabled === undefined ? false : currentEnabled);
        console.debug('togglePaymentMethod called with:', { methodId, currentEnabled, newEnabled });
        // Update the enabled flag directly on the paymentMethodsInfo for this method
        await updatePaymentMethodInfo(methodId, 'enabled', newEnabled);
    };

    // Removed manual expand/collapse; kept placeholder if future logic needed

    const getMethodSummary = (methodId, methodInfo) => {
        if (!methodInfo) return '';
        switch (methodId) {
            case 'instapay':
                return methodInfo.instapayAddress ? methodInfo.instapayAddress : t('Not configured');
            case 'bankTransfer':
                return methodInfo.bankDetails?.accountName ? methodInfo.bankDetails.accountName : t('No account set');
            case 'digitalWallet':
                return methodInfo.walletNumber ? methodInfo.walletNumber : t('No wallet number provided');
            case 'cash':
                return t('not Pay on arrival');
            default:
                return '';
        }
    };

    // Handle payment method info updates (no processingFee, description, instructions)
    const updatePaymentMethodInfo = async (methodId, field, value) => {
        console.debug('updatePaymentMethodInfo called with:', { methodId, field, value });
        console.debug('Current settings before update:', settings.bookingPolicies.paymentPolicy.paymentMethodsInfo);

        // Ensure the methodInfo object exists before spreading, with proper defaults
        const currentMethodInfo = settings.bookingPolicies.paymentPolicy.paymentMethodsInfo[methodId] || initializePaymentMethodInfo(methodId);

        const updates = {
            bookingPolicies: {
                ...settings.bookingPolicies,
                paymentPolicy: {
                    ...settings.bookingPolicies.paymentPolicy,
                    paymentMethodsInfo: {
                        ...settings.bookingPolicies.paymentPolicy.paymentMethodsInfo,
                        [methodId]: {
                            ...currentMethodInfo,
                            [field]: value
                        }
                    }
                }
            }
        };

        console.debug('Updates object:', updates);
        await updateSettings(updates);
    };

    // Handle nested payment method info updates (like bank details)
    const updateNestedPaymentMethodInfo = async (methodId, nestedField, field, value) => {
        console.debug('updateNestedPaymentMethodInfo called with:', { methodId, nestedField, field, value });

        // Ensure nested objects exist before spreading, with proper defaults
        const currentMethodInfo = settings.bookingPolicies.paymentPolicy.paymentMethodsInfo[methodId] || initializePaymentMethodInfo(methodId);
        const currentNestedInfo = currentMethodInfo[nestedField] || {};

        const updates = {
            bookingPolicies: {
                ...settings.bookingPolicies,
                paymentPolicy: {
                    ...settings.bookingPolicies.paymentPolicy,
                    paymentMethodsInfo: {
                        ...settings.bookingPolicies.paymentPolicy.paymentMethodsInfo,
                        [methodId]: {
                            ...currentMethodInfo,
                            [nestedField]: {
                                ...currentNestedInfo,
                                [field]: value
                            }
                        }
                    }
                }
            }
        };

        console.debug('Nested updates object:', updates);
        await updateSettings(updates);
    };

    // Toggle payment method enabled status
    const togglePaymentMethodEnabled = async (methodId) => {
        const currentEnabled = settings.bookingPolicies.paymentPolicy.paymentMethodsInfo[methodId]?.enabled;
        const newEnabled = currentEnabled === undefined ? false : !currentEnabled;
        console.debug('togglePaymentMethodEnabled called with:', { methodId, currentEnabled, newEnabled });
        await updatePaymentMethodInfo(methodId, 'enabled', newEnabled);
    };

    // Initialize payment method info with defaults if not exists
    const initializePaymentMethodInfo = (methodId) => {
        const defaults = {
            instapay: { enabled: true, instapayAddress: '' },
            bankTransfer: {
                enabled: true,
                bankDetails: { accountName: '', accountNumber: '', bankName: '' }
            },
            cash: { enabled: true },
            digitalWallet: { enabled: true, walletNumber: '' }
        };

        return defaults[methodId] || { enabled: true };
    };

    // Validate Instapay address format
    const validateInstapayAddress = (address) => {
        const pattern = /^[a-zA-Z0-9._-]+@instapay$/;
        return !address || pattern.test(address);
    };

    if (loading) {
        return (
            <div className="bg-babyblue-50 big-card container-fluid flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <section className='bg-babyblue-50 big-card container-fluid'>
            <div className="pt-4 pb-3 px-2 mx-3 space-y-5">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-900 mb-2">
                                {t('bookingPolicies')}
                            </h1>
                            <p className="text-slate-600">
                                {t('bookingPoliciesDescription')}
                            </p>
                        </div>
                        <div className="flex items-center space-x-3 rtl:space-x-reverse">
                            <button
                                onClick={() => saveBookingPolicies(settings)}
                                disabled={loading}
                                className="flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {loading ? (
                                    <Loader2 size={20} className="animate-spin" />
                                ) : (
                                    <Save size={20} />
                                )}
                                <span>{loading ? t('Saving...') : t('Save Changes')}</span>
                            </button>
                        </div>
                    </div>

                    {/* Status Messages */}
                    {/* {error && (
                        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
                            <div className="flex items-start space-x-3 rtl:space-x-reverse">
                                <AlertTriangle size={20} className="text-red-600 mt-0.5" />
                                <div>
                                    <h4 className="font-semibold text-red-900 mb-1">
                                        {t('Error')}
                                    </h4>
                                    <p className="text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )} */}

                    {/* {showSuccessMessage && (
                        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4">
                            <div className="flex items-start space-x-3 rtl:space-x-reverse">
                                <CheckCircle size={20} className="text-green-600 mt-0.5" />
                                <div>
                                    <h4 className="font-semibold text-green-900 mb-1">
                                        {t('Settings Saved Successfully')}
                                    </h4>
                                    <p className="text-green-700">
                                        {t('Portal settings have been updated successfully')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {hasUnsavedChanges && (
                        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                            <div className="flex items-start space-x-3 rtl:space-x-reverse">
                                <AlertTriangle size={20} className="text-yellow-600 mt-0.5" />
                                <div>
                                    <h4 className="font-semibold text-yellow-900 mb-1">
                                        {t('Unsaved Changes')}
                                    </h4>
                                    <p className="text-yellow-700">
                                        {t('You have unsaved changes. Save them before publishing.')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )} */}
                </div>

                {/* Check-in/Check-out Policy */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center space-x-2 rtl:space-x-reverse">
                        <Clock size={20} className="text-blue-600" />
                        <span>{t('Check-in/Check-out Policy')}</span>
                    </h3>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Check-in Policy */}
                        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                            <h4 className="font-medium text-green-900 mb-4">
                                {t('Check-in Policy')}
                            </h4>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        {t('Standard Check-in Time')}
                                    </label>
                                    <input
                                        type="time"
                                        value={settings.bookingPolicies.checkInPolicy.standardTime}
                                        onChange={(e) => handlePolicyChange('checkInPolicy', 'standardTime', e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        {t('Early Check-in Fee')} ({t('EGP')})
                                    </label>
                                    <input
                                        type="number"
                                        value={settings.bookingPolicies.checkInPolicy.earlyCheckInFee}
                                        onChange={(e) => handlePolicyChange('checkInPolicy', 'earlyCheckInFee', parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        {t('Late Arrival Cutoff')}
                                    </label>
                                    <input
                                        type="time"
                                        value={settings.bookingPolicies.checkInPolicy.lateArrivalCutoff}
                                        onChange={(e) => handlePolicyChange('checkInPolicy', 'lateArrivalCutoff', e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        {t('Late Arrival Fee')} ({t('EGP')})
                                    </label>
                                    <input
                                        type="number"
                                        value={settings.bookingPolicies.checkInPolicy.lateArrivalFee}
                                        onChange={(e) => handlePolicyChange('checkInPolicy', 'lateArrivalFee', parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        min="0"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Check-out Policy */}
                        <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                            <h4 className="font-medium text-orange-900 mb-4">
                                {t('Check-out Policy')}
                            </h4>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        {t('Standard Check-out Time')}
                                    </label>
                                    <input
                                        type="time"
                                        value={settings.bookingPolicies.checkOutPolicy.standardTime}
                                        onChange={(e) => handlePolicyChange('checkOutPolicy', 'standardTime', e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        {t('Late Check-out Fee')} ({t('EGP')})
                                    </label>
                                    <input
                                        type="number"
                                        value={settings.bookingPolicies.checkOutPolicy.lateCheckOutFee}
                                        onChange={(e) => handlePolicyChange('checkOutPolicy', 'lateCheckOutFee', parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        {t('Grace Period (Minutes)')}
                                    </label>
                                    <input
                                        type="number"
                                        value={settings.bookingPolicies.checkOutPolicy.gracePeriodMinutes}
                                        onChange={(e) => handlePolicyChange('checkOutPolicy', 'gracePeriodMinutes', parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                        min="0"
                                        max="180"
                                    />
                                    <p className="text-xs text-slate-500 mt-1">
                                        {t('Free period after checkout time')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payment Policy */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center space-x-2 rtl:space-x-reverse">
                        <DollarSign size={20} className="text-blue-600" />
                        <span>{t('Payment Policy')}</span>
                    </h3>
                    <div className="space-y-4">
                        {/* Payment Integration Toggle */}
                        <div className="flex items-center">
                            <input
                                id='paymentIntegrationEnabled'
                                type="checkbox"
                                checked={settings.bookingPolicies.paymentPolicy.paymentIntegrationEnabled || false}
                                onChange={(e) => handlePolicyChange('paymentPolicy', 'paymentIntegrationEnabled', e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor='paymentIntegrationEnabled' className="ml-3 rtl:mr-3 block text-sm font-medium text-slate-700">
                                {t("paymentIntegrationEnabled")}
                            </label>
                        </div>

                        {/* Show message when payment integration is disabled */}
                        {settings.bookingPolicies.paymentPolicy.paymentIntegrationEnabled && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mt-4">
                                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                                    <Info size={20} className="text-yellow-600" />
                                    <p className="text-yellow-800 text-sm">
                                        {t('Payment methods have been disabled. The electronic payment gateway has been activated.')}
                                    </p>
                                </div>
                            </div>
                        )}

                        {settings.bookingPolicies.paymentPolicy.depositRequired && (
                            <div className="pl-7">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            {t('Deposit Percentage (%)')}
                                        </label>
                                        <input
                                            type="number"
                                            value={settings.bookingPolicies.paymentPolicy.depositPercentage}
                                            onChange={(e) => handlePolicyChange('paymentPolicy', 'depositPercentage', parseInt(e.target.value) || 0)}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            min="0"
                                            max="100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            {t('Payment Due Date')}
                                        </label>
                                        <select
                                            value={settings.bookingPolicies.paymentPolicy.paymentDueDate}
                                            onChange={(e) => handlePolicyChange('paymentPolicy', 'paymentDueDate', e.target.value)}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="booking">{t('At Booking')}</option>
                                            <option value="check-in">{t('At Check-in')}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Payment Methods Configuration - only show if integration is enabled */}
                        {!settings.bookingPolicies.paymentPolicy.paymentIntegrationEnabled && (
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-4">
                                    {t('Payment Methods Configuration')}
                                </label>
                                <div className="space-y-6">
                                    {paymentMethods.map((method) => {
                                        const methodInfo = settings.bookingPolicies.paymentPolicy.paymentMethodsInfo?.[method.id] || initializePaymentMethodInfo(method.id);
                                        const isEnabled = methodInfo.enabled !== false;

                                        return (
                                            <div
                                                key={method.id}
                                                className={`rounded-lg border p-6 transition-all duration-200 ${isEnabled ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                                                    }`}
                                            >
                                                {/* Payment Method Header */}
                                                <div className={`flex items-center justify-between ${isEnabled ? ' mb-4' : ''}`}>
                                                    <div>
                                                        <div className={`flex items-center space-x-3 ${isEnabled ? '' : 'opacity-60'}`}>
                                                            <span className={`p-2 rounded-full ${isEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                                                                {method.icon}
                                                            </span>
                                                            <div>
                                                                <h4 className="text-lg font-semibold text-slate-900 mb-0 ms-2">{method.label}</h4>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            {!isEnabled && <p className="text-xs text-slate-500 ps-5 mt-0.5">{getMethodSummary(method.id, methodInfo)}</p>}
                                                        </div>
                                                    </div>


                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={isEnabled}
                                                            onChange={() => togglePaymentMethodEnabled(method.id)}
                                                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                        />
                                                        <span className="text-sm font-medium text-slate-700 ms-2">{t('enable')}</span>
                                                    </label>
                                                </div>

                                                {/* Auto-shown details when enabled */}
                                                {isEnabled && (
                                                    <div className="space-y-4">
                                                        {/* Instapay Specific Fields */}
                                                        {method.id === 'instapay' && (
                                                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                                                                <h5 className="text-sm font-semibold text-slate-900 mb-3">{t('Instapay Configuration')}</h5>
                                                                <div>
                                                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                                                        {t('Instapay Address')}
                                                                    </label>
                                                                    <div className="relative">
                                                                        <input
                                                                            type="text"
                                                                            value={methodInfo.instapayAddress || ''}
                                                                            onChange={(e) => updatePaymentMethodInfo(method.id, 'instapayAddress', e.target.value)}
                                                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${validateInstapayAddress(methodInfo.instapayAddress)
                                                                                ? 'border-slate-300 focus:ring-blue-500'
                                                                                : 'border-red-300 focus:ring-red-500'
                                                                                }`}
                                                                            placeholder="example@instapay"
                                                                            pattern="^[a-zA-Z0-9._-]+@instapay$"
                                                                        />
                                                                        <div className="mt-1">
                                                                            {!validateInstapayAddress(methodInfo.instapayAddress) ? (
                                                                                <p className="text-xs text-red-600">
                                                                                    {t('Invalid format. Must be username@instapay (e.g., example@instapay)')}
                                                                                </p>
                                                                            ) : (
                                                                                <p className="text-xs text-slate-500">
                                                                                    {t('Format')}: username@instapay (e.g., example@instapay)
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Bank Transfer Specific Fields */}
                                                        {method.id === 'bankTransfer' && (
                                                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                                                                <h5 className="text-sm font-semibold text-slate-900 mb-3">{t('Bank Account Details')}</h5>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                                                            {t('Account Name')}
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            value={methodInfo.bankDetails?.accountName || ''}
                                                                            onChange={(e) => updateNestedPaymentMethodInfo(method.id, 'bankDetails', 'accountName', e.target.value)}
                                                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                                                            placeholder={t('Enter account holder name')}
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                                                            {t('Account Number')}
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            value={methodInfo.bankDetails?.accountNumber || ''}
                                                                            onChange={(e) => updateNestedPaymentMethodInfo(method.id, 'bankDetails', 'accountNumber', e.target.value)}
                                                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                                                            placeholder={t('Enter account number')}
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                                                            {t('Bank Name')}
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            value={methodInfo.bankDetails?.bankName || ''}
                                                                            onChange={(e) => updateNestedPaymentMethodInfo(method.id, 'bankDetails', 'bankName', e.target.value)}
                                                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                                                            placeholder={t('Enter bank name')}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Digital Wallet Specific Fields */}
                                                        {method.id === 'digitalWallet' && (
                                                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                                                                <h5 className="text-sm font-semibold text-slate-900 mb-3">{t('Digital Wallet Number')}</h5>
                                                                <input
                                                                    type="text"
                                                                    value={methodInfo.walletNumber || ''}
                                                                    onChange={(e) => {
                                                                        console.debug('Digital wallet number update:', {
                                                                            methodId: method.id,
                                                                            newValue: e.target.value
                                                                        });
                                                                        updatePaymentMethodInfo(method.id, 'walletNumber', e.target.value);
                                                                    }}
                                                                    placeholder={t('Enter wallet number or account ID')}
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Cancellation Policy */}
                {/* <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center space-x-2 rtl:space-x-reverse">
                        <AlertTriangle size={20} className="text-blue-600" />
                        <span>{t('Cancellation Policy')}</span>
                    </h3>

                    <div className="space-y-4">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                checked={settings.bookingPolicies.cancellationPolicy.enabled}
                                onChange={(e) => handlePolicyChange('cancellationPolicy', 'enabled', e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label className="ml-3 block text-sm font-medium text-slate-700">
                                {t('Enable Cancellation Policy')}
                            </label>
                        </div>

                        {settings.bookingPolicies.cancellationPolicy.enabled && (
                            <div className="pl-7">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            {t('Free Cancellation Hours')}
                                        </label>
                                        <input
                                            type="number"
                                            value={settings.bookingPolicies.cancellationPolicy.freeHours}
                                            onChange={(e) => handlePolicyChange('cancellationPolicy', 'freeHours', parseInt(e.target.value) || 0)}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            min="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            {t('Partial Refund Hours')}
                                        </label>
                                        <input
                                            type="number"
                                            value={settings.bookingPolicies.cancellationPolicy.partialRefundHours}
                                            onChange={(e) => handlePolicyChange('cancellationPolicy', 'partialRefundHours', parseInt(e.target.value) || 0)}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            min="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            {t('Refund Percentage (%)')}
                                        </label>
                                        <input
                                            type="number"
                                            value={settings.bookingPolicies.cancellationPolicy.refundPercentage}
                                            onChange={(e) => handlePolicyChange('cancellationPolicy', 'refundPercentage', parseInt(e.target.value) || 0)}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            min="0"
                                            max="100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            {t('No Refund Hours')}
                                        </label>
                                        <input
                                            type="number"
                                            value={settings.bookingPolicies.cancellationPolicy.noRefundHours}
                                            onChange={(e) => handlePolicyChange('cancellationPolicy', 'noRefundHours', parseInt(e.target.value) || 0)}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            min="0"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div> */}

                {/* Guest Policies */}
                {/* <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"> */}
                {/* <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center space-x-2 rtl:space-x-reverse">
                        <Users size={20} className="text-blue-600" />
                        <span>{t('Guest Policies')}</span>
                    </h3> */}

                {/* <div className="space-y-4"> */}
                {/* <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                {t('Minimum Guest Age')}
                            </label>
                            <input
                                type="number"
                                value={settings.bookingPolicies.guestPolicies.minimumAge}
                                onChange={(e) => handlePolicyChange('guestPolicies', 'minimumAge', parseInt(e.target.value) || 0)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                min="0"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                {t('Max Guests Per Unit')}
                            </label>
                            <input
                                type="number"
                                value={settings.bookingPolicies.guestPolicies.maxGuestsPerUnit}
                                onChange={(e) => handlePolicyChange('guestPolicies', 'maxGuestsPerUnit', parseInt(e.target.value) || 0)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                min="1"
                            />
                        </div> */}

                {/* Pet Policy */}
                {/* <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                {t('Pet Policy')}
                            </label>
                            <select
                                value={settings.bookingPolicies.guestPolicies.petPolicy}
                                onChange={(e) => handlePolicyChange('guestPolicies', 'petPolicy', e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="not-allowed">{t('Not Allowed')}</option>
                                <option value="allowed-with-fee">{t('Allowed with Fee')}</option>
                                <option value="allowed-free">{t('Allowed Free')}</option>
                            </select>
                        </div>
                        {settings.bookingPolicies.guestPolicies.petPolicy !== 'not-allowed' && (
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    {t('Pet Fee')} ({t('EGP')})
                                </label>
                                <input
                                    type="number"
                                    value={settings.bookingPolicies.guestPolicies.petFee}
                                    onChange={(e) => handlePolicyChange('guestPolicies', 'petFee', parseInt(e.target.value) || 0)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    min="0"
                                />
                            </div>
                        )} */}

                {/* Smoking Policy */}
                {/* <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                {t('Smoking Policy')}
                            </label>
                            <select
                                value={settings.bookingPolicies.guestPolicies.smokingPolicy}
                                onChange={(e) => handlePolicyChange('guestPolicies', 'smokingPolicy', e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="not-allowed">{t('Not Allowed')}</option>
                                <option value="allowed">{t('Allowed')}</option>
                            </select>
                        </div> */}

                {/* Party Policy */}
                {/* <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                {t('Party Policy')}
                            </label>
                            <select
                                value={settings.bookingPolicies.guestPolicies.partyPolicy}
                                onChange={(e) => handlePolicyChange('guestPolicies', 'partyPolicy', e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="not-allowed">{t('Not Allowed')}</option>
                                <option value="allowed">{t('Allowed')}</option>
                            </select>
                        </div> */}
                {/* </div> */}
                {/* </div> */}
            </div>
        </section>
    );
};